import React, { useState } from 'react';
import { Mail, MapPin, Clock, Shield, Truck, Award } from 'react-feather';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import FeaturedProducts from './FeaturedProducts';
import Modal from './components/UploadModal';
import { Carousel } from 'react-responsive-carousel';
import "react-responsive-carousel/lib/styles/carousel.min.css";
import { Services } from './components/Services';
import NewsletterAndBlogSection from './components/Blog';
import CountUpStats from './components/Stats';

const LandingPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();

  const toggleModal = () => {
    // For Hero page, we still use the modal as it provides information about WhatsApp prescriptions
    setIsModalOpen(!isModalOpen);
  };

  return (
    <div className="font-sans">
      {/* Header Section */}
      <header className="w-full bg-gradient-to-br from-primary-50 via-white to-secondary-50 relative overflow-hidden">
        {/* Modern Top Info Bar with Glassmorphism */}
        <div className="bg-gradient-to-r from-primary-600 via-primary-700 to-secondary-600 text-white relative">
          <div className="absolute inset-0 bg-black/10"></div>
          <div className="container mx-auto px-4 relative z-10">
            <div className="flex flex-col md:flex-row justify-between items-center py-4 space-y-3 md:space-y-0">
              <div className="flex items-center space-x-8">
                <motion.div
                  className="flex items-center space-x-2 bg-white/10 backdrop-blur-sm rounded-full px-3 py-1"
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.2 }}
                >
                  <Clock className="h-4 w-4" />
                  <span className="text-sm font-medium">Mon-Fri: 09:00 - 17:00</span>
                </motion.div>
                <motion.div
                  className="hidden md:flex items-center space-x-2 bg-white/10 backdrop-blur-sm rounded-full px-3 py-1"
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.2 }}
                >
                  <Shield className="h-4 w-4" />
                  <span className="text-sm font-medium">100% Genuine Products</span>
                </motion.div>
              </div>
              <div className="flex flex-col md:flex-row items-center space-y-2 md:space-y-0 md:space-x-6">
                <motion.div
                  className="flex items-center space-x-2 bg-white/10 backdrop-blur-sm rounded-full px-3 py-1"
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.2 }}
                >
                  <Mail className="h-4 w-4" />
                  <span className="text-sm">maxmapharmacy@gmail.com</span>
                </motion.div>
                <motion.div
                  className="flex items-center space-x-2 bg-white/10 backdrop-blur-sm rounded-full px-3 py-1"
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.2 }}
                >
                  <MapPin className="h-4 w-4" />
                  <span className="text-sm">Nairobi, Kenya</span>
                </motion.div>
              </div>
            </div>
          </div>
        </div>

        {/* Modern Carousel for ads */}
        <div className="w-full bg-gradient-to-r from-neutral-50 to-white relative overflow-hidden h-[200px] sm:h-[300px] md:h-[400px] lg:h-[450px]">
          <div className="absolute inset-0 bg-gradient-to-r from-primary-500/5 to-secondary-500/5"></div>
          <Carousel
            showArrows={true}
            showThumbs={false}
            infiniteLoop
            autoPlay
            interval={5000}
            showStatus={false}
            className="h-full modern-carousel"
          >
            <div className="h-full w-full flex items-center justify-center relative">
              <div className="absolute inset-0 bg-gradient-to-br from-primary-100/20 to-secondary-100/20 backdrop-blur-sm"></div>
              <motion.img
                src={process.env.PUBLIC_URL + '/carousel.png'}
                alt="Dawamtaani Pharmacy Ad 1"
                className="w-full h-full object-cover md:object-contain relative z-10 drop-shadow-lg"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.3 }}
              />
            </div>
            <div className="h-full w-full flex items-center justify-center relative bg-white">
              <div className="absolute inset-0 bg-gradient-to-br from-secondary-100/20 to-accent-100/20 backdrop-blur-sm"></div>
              <motion.img
                src={process.env.PUBLIC_URL + '/logo512.png'}
                alt="Dawamtaani Pharmacy Ad 2"
                className="w-full h-full object-contain relative z-10 drop-shadow-lg p-8"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.3 }}
              />
            </div>
            <div className="h-full w-full flex items-center justify-center relative bg-white">
              <div className="absolute inset-0 bg-gradient-to-br from-accent-100/20 to-primary-100/20 backdrop-blur-sm"></div>
              <motion.img
                src={process.env.PUBLIC_URL + '/logo512.png'}
                alt="Dawamtaani Pharmacy Ad 3"
                className="w-full h-full object-contain relative z-10 drop-shadow-lg p-8"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.3 }}
              />
            </div>
          </Carousel>
        </div>

        {/* Enhanced Hero Content */}
        <div className="container mx-auto px-4 py-20 md:py-32 relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <motion.div
              className="text-center lg:text-left relative z-10"
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
            >
              <motion.div
                className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-secondary-100 to-secondary-50 text-secondary-700 rounded-full text-sm font-semibold mb-8 shadow-soft border border-secondary-200/50 backdrop-blur-sm"
                whileHover={{ scale: 1.05, y: -2 }}
                transition={{ duration: 0.2 }}
              >
                <Award className="h-4 w-4 mr-2" />
                Trusted Healthcare Partner
              </motion.div>

              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-neutral-900 mb-8 leading-[1.1]">
                Your Health,
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-600 via-primary-700 to-secondary-600 block mt-2"> Our Priority</span>
              </h1>

              <p className="text-xl md:text-2xl text-neutral-600 mb-10 leading-relaxed font-medium">
                Experience seamless healthcare with genuine medications, expert consultations, and personalized care.
                <span className="text-primary-600 font-semibold"> Your trusted pharmacy partner</span>, available whenever you need us most.
              </p>

              {/* Modern Feature Pills */}
              <div className="flex flex-wrap gap-4 mb-12 justify-center lg:justify-start">
                <motion.div
                  className="flex items-center px-6 py-3 bg-white/80 backdrop-blur-md rounded-full shadow-medium border border-primary-100 hover:shadow-large hover:border-primary-200 transition-all duration-300"
                  whileHover={{ scale: 1.05, y: -2 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="w-8 h-8 bg-gradient-to-r from-primary-500 to-primary-600 rounded-full flex items-center justify-center mr-3">
                    <Shield className="h-4 w-4 text-white" />
                  </div>
                  <span className="text-sm font-semibold text-neutral-800">100% Genuine</span>
                </motion.div>
                <motion.div
                  className="flex items-center px-6 py-3 bg-white/80 backdrop-blur-md rounded-full shadow-medium border border-secondary-100 hover:shadow-large hover:border-secondary-200 transition-all duration-300"
                  whileHover={{ scale: 1.05, y: -2 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="w-8 h-8 bg-gradient-to-r from-secondary-500 to-secondary-600 rounded-full flex items-center justify-center mr-3">
                    <Truck className="h-4 w-4 text-white" />
                  </div>
                  <span className="text-sm font-semibold text-neutral-800">Fast Delivery</span>
                </motion.div>
                <motion.div
                  className="flex items-center px-6 py-3 bg-white/80 backdrop-blur-md rounded-full shadow-medium border border-accent-100 hover:shadow-large hover:border-accent-200 transition-all duration-300"
                  whileHover={{ scale: 1.05, y: -2 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="w-8 h-8 bg-gradient-to-r from-accent-500 to-accent-600 rounded-full flex items-center justify-center mr-3">
                    <Award className="h-4 w-4 text-white" />
                  </div>
                  <span className="text-sm font-semibold text-neutral-800">Expert Care</span>
                </motion.div>
              </div>

              {/* Modern CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-6 justify-center lg:justify-start">
                <motion.button
                  className="group relative px-10 py-5 bg-gradient-to-r from-primary-600 via-primary-700 to-primary-800 text-white rounded-2xl font-bold shadow-large hover:shadow-xl transform hover:-translate-y-2 transition-all duration-300 overflow-hidden"
                  onClick={() => navigate('/shop')}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-primary-500 to-secondary-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <span className="relative z-10 flex items-center justify-center">
                    Shop Products
                    <svg className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </span>
                </motion.button>
                <motion.button
                  className="group relative px-10 py-5 bg-white/90 backdrop-blur-md text-primary-700 border-2 border-primary-200 rounded-2xl font-bold hover:bg-primary-50 hover:border-primary-400 hover:text-primary-800 transition-all duration-300 shadow-medium hover:shadow-large transform hover:-translate-y-1"
                  onClick={toggleModal}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <span className="flex items-center justify-center">
                    Upload Prescription
                    <svg className="w-5 h-5 ml-2 group-hover:rotate-12 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                  </span>
                </motion.button>
              </div>
            </motion.div>

            {/* Enhanced Right Content - Hero Image */}
            <motion.div
              className="relative"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
            >
              <div className="relative z-10">
                <motion.img
                  src="https://images.unsplash.com/photo-1559757148-5c350d0d3c56?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                  alt="Modern Pharmacy"
                  className="w-full h-auto rounded-3xl shadow-xl border border-white/20"
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.3 }}
                />

                {/* Enhanced Floating Cards */}
                <motion.div
                  className="absolute -top-8 -left-8 bg-white/95 backdrop-blur-md p-6 rounded-2xl shadow-xl border border-white/30"
                  animate={{ y: [0, -15, 0] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                  whileHover={{ scale: 1.05 }}
                >
                  <div className="flex items-center space-x-4">
                    <div className="w-14 h-14 bg-gradient-to-br from-secondary-500 to-secondary-600 rounded-2xl flex items-center justify-center shadow-medium">
                      <Shield className="h-7 w-7 text-white" />
                    </div>
                    <div>
                      <p className="font-bold text-neutral-900 text-lg">Verified</p>
                      <p className="text-sm text-neutral-600 font-medium">100% Genuine</p>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  className="absolute -bottom-8 -right-8 bg-white/95 backdrop-blur-md p-6 rounded-2xl shadow-xl border border-white/30"
                  animate={{ y: [0, 15, 0] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 2 }}
                  whileHover={{ scale: 1.05 }}
                >
                  <div className="flex items-center space-x-4">
                    <div className="w-14 h-14 bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl flex items-center justify-center shadow-medium">
                      <Truck className="h-7 w-7 text-white" />
                    </div>
                    <div>
                      <p className="font-bold text-neutral-900 text-lg">Fast Delivery</p>
                      <p className="text-sm text-neutral-600 font-medium">Same Day</p>
                    </div>
                  </div>
                </motion.div>
              </div>

              {/* Enhanced Background Decorations */}
              <div className="absolute inset-0 bg-gradient-to-br from-primary-200/30 via-secondary-200/30 to-accent-200/30 rounded-3xl transform rotate-3 -z-10 blur-sm"></div>
              <div className="absolute inset-0 bg-gradient-to-tl from-primary-100/40 to-secondary-100/40 rounded-3xl transform -rotate-2 -z-20 blur-lg"></div>
            </motion.div>
          </div>
        </div>
      </header>

      {/* Featured Products Section */}
      <FeaturedProducts />

      <Services />
      <CountUpStats />

      <NewsletterAndBlogSection />

      {/* Modal Component */}
      <AnimatePresence>
        {isModalOpen && (
          <Modal key="hero-upload-modal" closeModal={toggleModal} />
        )}
      </AnimatePresence>
    </div>
  );
};

export default LandingPage;
